/**
 * The shared menu (ADR 0194, decisions 2, 3 and 5), as the owner's "Block
 * Interactions" prototype has it: the popup's Dismiss ▾ and More ⋯, a card's
 * right-click menu, and the selection bar's Dismiss. How to open one is in
 * `useMenu.ts`.
 *
 * - **Where:** at a point (a right-click), flipping left or up near the
 *   window's edges and keeping 8 px clear; or below a button, above it when
 *   there's no room. A submenu opens to the right, level with its item, or
 *   to the left. On the page in a portal, above dialogs; opened from inside
 *   a dialog, inside the dialog's element, so its focus trap and a click
 *   outside it count the menu as inside.
 * - **Keys:** focus starts on the first item that can be chosen. ↑ ↓ wrap,
 *   passing rules and greyed items; Home and End; a letter jumps to the
 *   next item starting with it. → or Enter opens a submenu on its first
 *   item; ← or Esc closes just that submenu. Enter or Space chooses. Esc
 *   closes the menu and gives focus back to what opened it; Tab closes it
 *   and focus moves on from there.
 * - **The pointer:** the item under it has the focus. A submenu opens after
 *   150 ms over its item, and gives way to another item after 300 ms, so
 *   the pointer can cross a neighbour on its way in. A click chooses.
 * - **It closes** on a press outside it (which still does what it does), a
 *   scroll outside it, a resize, the window losing focus, or a choice.
 * - **Looks:** the app's tokens, in both themes; it fades in over 90 ms,
 *   unless motion is reduced. Positions are set from script (`style.left`),
 *   never an inline <style>, which the CSP refuses (ADR 0191).
 */
import {
  type ComponentPropsWithRef,
  useEffect,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { placeAtPoint, placeFromAnchor, placeSubmenu, type Rect, type Spot } from "./menuPlacement";
import {
  markDismissPress,
  MENU_PANEL,
  type MenuControl,
  type MenuEntry,
  type MenuHeader,
  type MenuItem,
  type MenuRequest,
  useMenu,
} from "./useMenu";

// A submenu opens after this long over its item, and gives way to another
// item's after this long, so the pointer can cross a neighbour (prototype).
export const SUBMENU_OPEN_MS = 150;
export const SUBMENU_SWITCH_MS = 300;
// A scroll this soon after opening is the page settling (a peek folding), not the user.
const SCROLL_GRACE_MS = 150;

const DIALOG = '[role="dialog"], [role="alertdialog"]';
const ITEM = '[role="menuitem"]';

function isItem(entry: MenuEntry | undefined): entry is MenuItem {
  return entry !== undefined && !("separator" in entry);
}

// No rule first, last, or after another.
function tidy(entries: readonly MenuEntry[]): readonly MenuEntry[] {
  const out: MenuEntry[] = [];
  for (const entry of entries) {
    if (!isItem(entry) && (out.length === 0 || !isItem(out[out.length - 1]))) {
      continue;
    }
    out.push(entry);
  }
  while (out.length > 0 && !isItem(out[out.length - 1])) {
    out.pop();
  }
  return out;
}

function canChoose(entry: MenuEntry | undefined): boolean {
  return isItem(entry) && entry.disabled !== true;
}

function rectOf(element: Element): Rect {
  const box = element.getBoundingClientRect();
  return { left: box.left, top: box.top, right: box.right, bottom: box.bottom };
}

function windowSize(): { width: number; height: number } {
  const root = document.documentElement;
  return { width: root.clientWidth || window.innerWidth, height: root.clientHeight || window.innerHeight };
}

/** The open menu, if any. Render it beside what opens it (useMenu.ts). */
export function Menu({ control }: { control: MenuControl }) {
  if (control.request === null) {
    return null;
  }
  return <OpenMenu key={control.request.serial} request={control.request} dismiss={control.dismiss} />;
}

interface Submenu {
  // Its item's index in the level above.
  parent: number;
  entries: readonly MenuEntry[];
}

function OpenMenu({ request, dismiss }: { request: MenuRequest; dismiss: (restoreFocus: boolean) => void }) {
  const [root] = useState(() => tidy(request.items));
  const [subs, setSubsState] = useState<readonly Submenu[]>([]);
  // The same, for handlers between renders.
  const subsNow = useRef<readonly Submenu[]>([]);
  const panels = useRef<(HTMLDivElement | null)[]>([]);
  const placed = useRef(new WeakSet<HTMLElement>());
  // The level whose first item takes focus once it's on screen: the menu's own, as it opens.
  const focusLevel = useRef<number | null>(0);
  const hover = useRef<{ timer: number; level: number; at: (number | null)[] }>({ timer: 0, level: -1, at: [] });
  const openedAt = useRef(0);
  const ids = useId();
  // In the opener's dialog, if it's in one: inside its focus trap.
  const [host] = useState<HTMLElement>(
    () => (request.anchor ?? request.opener)?.closest<HTMLElement>(DIALOG) ?? document.body,
  );

  const setSubs = (next: readonly Submenu[]) => {
    subsNow.current = next;
    setSubsState(next);
  };
  const entriesAt = (level: number): readonly MenuEntry[] =>
    level === 0 ? root : (subsNow.current[level - 1]?.entries ?? []);
  const inMenu = (node: Node | null): boolean =>
    node !== null && panels.current.some((panel) => panel?.contains(node) === true);
  const itemAt = (level: number, index: number): HTMLElement | null =>
    panels.current[level]?.querySelector<HTMLElement>(`[data-index="${String(index)}"]`) ?? null;

  const focusItem = (level: number, index: number) => {
    const item = itemAt(level, index);
    const panel = panels.current[level];
    if (item === null || !panel) {
      return;
    }
    item.focus({ preventScroll: true });
    // Kept in view in the menu's own scroll, if it has one.
    if (item.offsetTop < panel.scrollTop) {
      panel.scrollTop = item.offsetTop - 4;
    } else if (item.offsetTop + item.offsetHeight > panel.scrollTop + panel.clientHeight) {
      panel.scrollTop = item.offsetTop + item.offsetHeight - panel.clientHeight + 4;
    }
  };

  const focusFirst = (level: number) => {
    const entries = entriesAt(level);
    let index = entries.findIndex(canChoose);
    if (index < 0) {
      index = entries.findIndex(isItem);
    }
    if (index < 0) {
      panels.current[level]?.focus({ preventScroll: true });
    } else {
      focusItem(level, index);
    }
  };

  const move = (level: number, from: number, by: 1 | -1 | "first" | "last") => {
    const choosable = entriesAt(level).flatMap((entry, index) => (canChoose(entry) ? [index] : []));
    if (choosable.length === 0) {
      return;
    }
    let k: number;
    if (by === "first") {
      k = 0;
    } else if (by === "last") {
      k = choosable.length - 1;
    } else {
      const at = choosable.indexOf(from);
      k = at < 0 ? (by > 0 ? 0 : choosable.length - 1) : (at + by + choosable.length) % choosable.length;
    }
    focusItem(level, choosable[k] ?? -1);
  };

  // A letter: the next item, after this one, that starts with it.
  const jump = (level: number, from: number, letter: string) => {
    const entries = entriesAt(level);
    for (let step = 1; step <= entries.length; step += 1) {
      const index = (from + step + entries.length) % entries.length;
      const entry = entries[index];
      if (canChoose(entry) && isItem(entry) && entry.label.toLowerCase().startsWith(letter)) {
        focusItem(level, index);
        return;
      }
    }
  };

  const stopHover = () => {
    window.clearTimeout(hover.current.timer);
    hover.current.timer = 0;
    hover.current.level = -1;
  };
  const startHover = (level: number, run: () => void, ms: number) => {
    stopHover();
    hover.current.level = level;
    hover.current.timer = window.setTimeout(() => {
      hover.current.timer = 0;
      hover.current.level = -1;
      run();
    }, ms);
  };

  // Closes the submenus opened from this level and deeper. Focus in one of
  // them, or `focusItem` asked, goes back to the item that opened them.
  const closeSubs = (level: number, focusParent: boolean) => {
    const open = subsNow.current;
    const first = open[level];
    if (first === undefined) {
      return;
    }
    const focusWasInside = panels.current
      .slice(level + 1)
      .some((panel) => panel?.contains(document.activeElement) === true);
    if (focusParent || focusWasInside) {
      focusItem(level, first.parent);
    }
    setSubs(open.slice(0, level));
  };

  const openSub = (level: number, index: number, focusIt: boolean) => {
    stopHover();
    const entry = entriesAt(level)[index];
    if (!isItem(entry) || entry.items === undefined || entry.disabled === true) {
      return;
    }
    if (subsNow.current[level]?.parent === index) {
      if (focusIt) {
        focusFirst(level + 1);
      }
      return;
    }
    focusLevel.current = focusIt ? level + 1 : null;
    setSubs([...subsNow.current.slice(0, level), { parent: index, entries: tidy(entry.items()) }]);
  };

  const choose = (level: number, index: number, keyboard: boolean) => {
    const entry = entriesAt(level)[index];
    if (!isItem(entry) || entry.disabled === true) {
      return;
    }
    if (entry.items !== undefined) {
      openSub(level, index, keyboard);
      return;
    }
    stopHover();
    // Focus goes back first, so whatever the choice moves it to wins.
    dismiss(true);
    entry.onSelect?.({ keyboard });
  };

  const onKey = (event: KeyboardEvent, level: number, index: number) => {
    const entry = entriesAt(level)[index];
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Home":
      case "End":
        closeSubs(level, false);
        move(
          level,
          index,
          event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : event.key === "Home" ? "first" : "last",
        );
        break;
      case "ArrowRight":
        if (isItem(entry) && entry.items !== undefined) {
          openSub(level, index, true);
        }
        break;
      case "ArrowLeft":
        if (level > 0) {
          closeSubs(level - 1, true);
        }
        break;
      case "Escape":
        if (subsNow.current.length > level) {
          // A submenu the pointer opened from here.
          closeSubs(level, false);
        } else if (level > 0) {
          closeSubs(level - 1, true);
        } else {
          stopHover();
          dismiss(true);
        }
        break;
      case "Enter":
      case " ":
        if (!event.repeat && index >= 0) {
          choose(level, index, true);
        }
        break;
      case "Tab":
        // Focus moves on from what opened the menu, as if from there.
        stopHover();
        dismiss(true);
        return;
      case "ContextMenu":
      case "F10":
        break;
      default:
        if (event.key.length !== 1) {
          return;
        }
        closeSubs(level, false);
        jump(level, index, event.key.toLowerCase());
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const onPointerMove = (level: number, event: PointerEvent) => {
    if (event.pointerType === "touch") {
      return;
    }
    const state = hover.current;
    // In a submenu: what was waiting above gives way, the pointer got here.
    if (state.level >= 0 && state.level < level) {
      stopHover();
    }
    for (let above = 0; above < level; above += 1) {
      state.at[above] = null;
    }
    const item = event.target instanceof Element ? event.target.closest<HTMLElement>(ITEM) : null;
    if (item === null) {
      return;
    }
    if (document.activeElement !== item) {
      item.focus({ preventScroll: true });
    }
    const index = Number(item.dataset.index);
    if (state.at[level] === index) {
      return;
    }
    state.at[level] = index;
    stopHover();
    const open = subsNow.current[level];
    if (open?.parent === index) {
      return;
    }
    const entry = entriesAt(level)[index];
    if (isItem(entry) && entry.items !== undefined && entry.disabled !== true) {
      startHover(
        level,
        () => {
          openSub(level, index, false);
        },
        open ? SUBMENU_SWITCH_MS : SUBMENU_OPEN_MS,
      );
    } else if (open) {
      startHover(
        level,
        () => {
          closeSubs(level, false);
        },
        SUBMENU_SWITCH_MS,
      );
    }
  };

  const onClick = (level: number, target: Element) => {
    const item = target.closest<HTMLElement>(ITEM);
    if (item === null) {
      return;
    }
    const index = Number(item.dataset.index);
    const entry = entriesAt(level)[index];
    if (isItem(entry) && entry.items !== undefined) {
      openSub(level, index, false);
    } else {
      choose(level, index, false);
    }
  };

  // Where a panel goes: set from script, after measuring it.
  const place = (panel: HTMLElement, level: number) => {
    panel.style.left = "0px";
    panel.style.top = "0px";
    // Where 0, 0 lands: the window's corner, unless something (a transformed
    // dialog) holds fixed boxes; then the difference is taken off.
    const box = panel.getBoundingClientRect();
    const size = { width: box.width, height: box.height };
    const view = windowSize();
    let spot: Spot;
    if (level > 0) {
      const parent = panels.current[level - 1];
      const item = itemAt(level - 1, subsNow.current[level - 1]?.parent ?? -1);
      spot =
        parent && item
          ? placeSubmenu(rectOf(item), rectOf(parent), size, view)
          : { left: box.left, top: box.top };
    } else if (request.anchor !== null) {
      const dialog = request.anchor.closest(DIALOG);
      spot = placeFromAnchor(rectOf(request.anchor), size, view, {
        within: dialog ? rectOf(dialog) : null,
        clearTop: request.clearOf ? rectOf(request.clearOf).top : rectOf(request.anchor).top,
      });
    } else {
      spot = placeAtPoint(request.point?.x ?? 0, request.point?.y ?? 0, size, view);
    }
    panel.style.left = `${String(Math.round(spot.left - box.left))}px`;
    panel.style.top = `${String(Math.round(spot.top - box.top))}px`;
  };

  // Each panel is placed once, before it's painted; then focus goes where it was asked to.
  const afterRender = useEffectEvent(() => {
    panels.current.forEach((panel, level) => {
      if (panel && !placed.current.has(panel)) {
        placed.current.add(panel);
        place(panel, level);
      }
    });
    if (focusLevel.current !== null) {
      focusFirst(focusLevel.current);
      focusLevel.current = null;
    }
  });
  useLayoutEffect(() => {
    afterRender();
  }, [subs]);

  // The menu panel an event happened in, and its level; null outside the menu.
  const panelOf = (target: EventTarget | null): { level: number; target: Element } | null => {
    const panel = target instanceof Element ? target.closest<HTMLElement>(`[${MENU_PANEL}]`) : null;
    return panel !== null && target instanceof Element && inMenu(panel)
      ? { level: Number(panel.dataset.level), target }
      : null;
  };

  // The menu's own events, taken at the window, before anything under it, so
  // the card or board the menu is rendered in (React passes a portal's
  // events up its tree) never sees them, and a dialog's Escape (Radix
  // listens on the document) never closes it for a key that's the menu's.
  const onWindowKey = useEffectEvent((event: KeyboardEvent) => {
    const at = panelOf(event.target);
    if (at === null || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const item = at.target.closest<HTMLElement>(ITEM);
    onKey(event, at.level, item ? Number(item.dataset.index) : -1);
  });
  const onMenuPointerMove = useEffectEvent((event: PointerEvent) => {
    const at = panelOf(event.target);
    if (at !== null) {
      onPointerMove(at.level, event);
    }
  });
  const onMenuMouseDown = useEffectEvent((event: MouseEvent) => {
    const at = panelOf(event.target);
    // A press on the header or a rule leaves focus where it is.
    if (at !== null && at.target.closest(ITEM) === null) {
      event.preventDefault();
    }
  });
  const onMenuClick = useEffectEvent((event: MouseEvent) => {
    const at = panelOf(event.target);
    if (at !== null) {
      event.stopPropagation();
      onClick(at.level, at.target);
    }
  });
  const onMenuContextMenu = useEffectEvent((event: MouseEvent) => {
    // ☰ pressed in the menu, or a right-click on it: not the browser's menu over it.
    if (panelOf(event.target) !== null) {
      event.preventDefault();
      event.stopPropagation();
    }
  });
  const onMenuFocusOut = useEffectEvent((event: FocusEvent) => {
    // Focus taken elsewhere closes it, but not by its own button (whose click does).
    const next = event.relatedTarget;
    if (panelOf(event.target) !== null && next instanceof Node && !inMenu(next) && request.anchor?.contains(next) !== true) {
      stopHover();
      dismiss(false);
    }
  });
  const onPress = useEffectEvent((event: PointerEvent) => {
    const target = event.target instanceof Node ? event.target : null;
    // A press on the menu's own button is the button's: its click closes it.
    if (inMenu(target) || (target !== null && request.anchor?.contains(target) === true)) {
      return;
    }
    stopHover();
    markDismissPress(event);
    dismiss(false);
  });
  const onScroll = useEffectEvent((event: Event) => {
    if (event.target instanceof Node && inMenu(event.target)) {
      return;
    }
    if (performance.now() - openedAt.current < SCROLL_GRACE_MS) {
      return;
    }
    stopHover();
    dismiss(inMenu(document.activeElement));
  });
  const onLeave = useEffectEvent(() => {
    stopHover();
    dismiss(inMenu(document.activeElement));
  });

  useEffect(() => {
    openedAt.current = performance.now();
    const state = hover.current;
    // On the window, in its capture phase: first of all.
    const listen = <K extends keyof WindowEventMap>(
      type: K,
      listener: (event: WindowEventMap[K]) => void,
      capture = true,
    ) => {
      window.addEventListener(type, listener, capture);
      return () => {
        window.removeEventListener(type, listener, capture);
      };
    };
    const stops = [
      listen("keydown", (event) => {
        onWindowKey(event);
      }),
      listen("pointermove", (event) => {
        onMenuPointerMove(event);
      }),
      listen("mousedown", (event) => {
        onMenuMouseDown(event);
      }),
      listen("click", (event) => {
        onMenuClick(event);
      }),
      listen("contextmenu", (event) => {
        onMenuContextMenu(event);
      }),
      listen("focusout", (event) => {
        onMenuFocusOut(event);
      }),
      listen("pointerdown", (event) => {
        onPress(event);
      }),
      listen("scroll", (event) => {
        onScroll(event);
      }),
      // The window's own, not its elements' (they don't bubble).
      listen("resize", () => {
        onLeave();
      }, false),
      listen("blur", () => {
        onLeave();
      }, false),
    ];
    return () => {
      for (const stop of stops) {
        stop();
      }
      window.clearTimeout(state.timer);
    };
  }, []);

  const levels: { entries: readonly MenuEntry[]; parent: number; label: string | undefined }[] = [
    { entries: root, parent: -1, label: request.label },
    ...subs.map((sub, level) => {
      const opener = (level === 0 ? root : subs[level - 1]?.entries)?.[sub.parent];
      return { entries: sub.entries, parent: sub.parent, label: isItem(opener) ? opener.label : undefined };
    }),
  ];

  return createPortal(
    levels.map((panel, level) => (
      <div
        key={`${String(level)}:${String(panel.parent)}`}
        ref={(element) => {
          panels.current[level] = element;
        }}
        role="menu"
        tabIndex={-1}
        aria-label={level === 0 && request.labelledBy !== undefined ? undefined : panel.label}
        aria-labelledby={level === 0 ? request.labelledBy : undefined}
        data-level={level}
        data-ui-menu=""
        className={panelClass}
      >
        {level === 0 && request.header ? <MenuHeading header={request.header} /> : null}
        <MenuRows entries={panel.entries} openIndex={subs[level]?.parent ?? null} idBase={`${ids}-${String(level)}`} />
      </div>
    )),
    host,
  );
}

const panelClass =
  "fixed z-50 max-h-[calc(100dvh-16px)] min-w-[236px] max-w-[min(360px,calc(100vw-16px))] overflow-y-auto " +
  "overscroll-contain rounded-[12px] border border-line bg-panel p-1 text-[13.5px] leading-[1.2] text-text " +
  "outline-none select-none motion-safe:animate-menu-in " +
  "shadow-[0_2px_6px_rgba(0,0,0,0.06),0_14px_36px_rgba(0,0,0,0.16)] " +
  "dark:shadow-[0_2px_6px_rgba(0,0,0,0.4),0_14px_36px_rgba(0,0,0,0.55)]";

const itemClass =
  "group/item flex min-h-8 items-center gap-2.5 rounded-[8px] pr-2.5 pl-2 whitespace-nowrap outline-none";

// One class per property and state, so no two of them compete.
function itemTone(item: MenuItem): { item: string; aside: string } {
  if (item.disabled === true) {
    return { item: "cursor-default text-muted focus:bg-panel-2", aside: "text-current" };
  }
  if (item.warn === true) {
    return {
      item: "cursor-pointer text-status-needs-client-fg focus:bg-status-needs-client-bg aria-expanded:bg-status-needs-client-bg",
      aside: "text-current",
    };
  }
  return {
    item:
      "cursor-pointer focus:bg-brand-tint focus:text-brand-ink-on-tint " +
      "aria-expanded:bg-brand-tint aria-expanded:text-brand-ink-on-tint",
    aside: "text-muted group-focus/item:text-current group-aria-expanded/item:text-current",
  };
}

function MenuHeading({ header }: { header: MenuHeader }) {
  return (
    <div aria-hidden="true" className="flex flex-col gap-0.5 px-2.5 pt-1.5 pb-[7px] text-[11.5px] leading-[1.3] text-muted">
      <span className="truncate font-[650]">{header.title}</span>
      {header.line !== undefined ? <span className="truncate">{header.line}</span> : null}
    </div>
  );
}

function MenuRows({
  entries,
  openIndex,
  idBase,
}: {
  entries: readonly MenuEntry[];
  // The item whose submenu is open.
  openIndex: number | null;
  idBase: string;
}) {
  // Icons line up in their own column, when any item has one.
  const withIcons = entries.some((entry) => isItem(entry) && entry.icon !== undefined);
  return entries.map((entry, index) => {
    if (!isItem(entry)) {
      return <div key={`rule-${String(index)}`} role="separator" className="mx-1.5 my-1 h-px bg-line" />;
    }
    const tone = itemTone(entry);
    const reasonShown = entry.disabled === true && entry.reason !== undefined;
    const hint = reasonShown ? entry.reason : entry.items !== undefined ? "▸" : entry.hint;
    const hintId = `${idBase}-${String(index)}`;
    return (
      <div
        key={`${String(index)}-${entry.label}`}
        role="menuitem"
        tabIndex={-1}
        data-index={index}
        aria-label={entry.ariaLabel}
        aria-disabled={entry.disabled === true ? true : undefined}
        aria-describedby={reasonShown ? hintId : undefined}
        aria-haspopup={entry.items !== undefined ? "menu" : undefined}
        aria-expanded={entry.items !== undefined ? openIndex === index : undefined}
        className={`${itemClass} ${tone.item}`}
      >
        {withIcons ? (
          <span
            aria-hidden="true"
            className={`flex size-4 shrink-0 items-center justify-center [&>svg]:size-[15px] ${tone.aside}`}
          >
            {entry.icon}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate">{entry.label}</span>
        {hint !== undefined && hint !== "" ? (
          <span id={hintId} aria-hidden="true" className={`ml-3.5 shrink-0 text-[11.5px] tabular-nums ${tone.aside}`}>
            {hint}
          </span>
        ) : null}
      </div>
    );
  });
}

/**
 * A button that opens the shared menu below it (Dismiss ▾, More ⋯): its
 * `aria-haspopup` and `aria-expanded` kept, a click or ↓ opens it, and a
 * click while it's open closes it. `items` may be a function, so the menu is
 * built as it opens. `clearOf` names the bar it sits in, if the menu should
 * clear the bar's top when it opens above (the selection bar).
 */
export function MenuButton({
  items,
  menuLabel,
  header,
  clearOf,
  id,
  type = "button",
  onClick,
  onKeyDown,
  ...props
}: Omit<ComponentPropsWithRef<"button">, "aria-haspopup" | "aria-expanded"> & {
  items: readonly MenuEntry[] | (() => readonly MenuEntry[]);
  /** The menu's name; by default it's named by the button. */
  menuLabel?: string | undefined;
  header?: MenuHeader | undefined;
  clearOf?: ((button: HTMLButtonElement) => Element | null) | undefined;
}) {
  const menu = useMenu();
  const ownId = useId();
  const open = (button: HTMLButtonElement) => {
    menu.openFrom(button, typeof items === "function" ? items() : items, {
      label: menuLabel,
      header,
      clearOf: clearOf?.(button) ?? null,
    });
  };
  return (
    <>
      <button
        {...props}
        id={id ?? ownId}
        type={type}
        aria-haspopup="menu"
        aria-expanded={menu.isOpen}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) {
            open(event.currentTarget);
          }
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (!event.defaultPrevented && event.key === "ArrowDown" && !menu.isOpen) {
            event.preventDefault();
            open(event.currentTarget);
          }
        }}
      />
      <Menu control={menu} />
    </>
  );
}
